/**
 * Dependency-free charts in the Bold-Friendly world. Data-viz is a first-class
 * member of the design system: the accent ramp (primary → cyan) drives series,
 * grids are hairline-faint (theme-aware --line), values use tabular figures. No
 * chart library — these stay small, themeable from tokens, and legible on both
 * the light and dark grounds.
 */

export interface SeriesPoint {
  label: string;
  value: number;
}

/* ── Bar chart ───────────────────────────────────────────────────────────── */
/*
 * A FLEX bar chart, not a stretched SVG. Columns and their axis labels live in
 * the same flow, so a bar always sits over its label and the value over its bar
 * at any panel width — nothing distorts horizontally the way a `width:100%` SVG
 * does, and a few categories spread evenly across the panel instead of clustering
 * at the left edge. The figure self-labels (value above each bar), so it needs no
 * duplicate number strip beneath it. The hairline quartile grid is drawn with a
 * repeating background on the plot area, consistent with the glass-world rules.
 */
export function BarChart({
  data,
  height = 180,
  format = (n) => String(n),
  ariaLabel,
}: {
  data: SeriesPoint[];
  height?: number;
  format?: (n: number) => string;
  ariaLabel: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <figure className="chart chart--bar" aria-label={ariaLabel}>
      <div className="chart__plot" style={{ height }}>
        {data.map((d) => {
          const pct = (d.value / max) * 100;
          return (
            <div className="chart__col" key={d.label} title={`${d.label}: ${format(d.value)}`}>
              <span className="chart__val num">{format(d.value)}</span>
              <span
                className="chart__bar"
                style={{ height: `${pct}%` }}
                data-zero={d.value === 0 ? '' : undefined}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>
      <figcaption className="chart__axis">
        {data.map((d) => (
          <span key={d.label} className="chart__tick">
            {d.label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/* ── Donut / proportion ring ─────────────────────────────────────────────── */
export function Donut({
  segments,
  size = 132,
  centerLabel,
  centerSub,
  ariaLabel,
}: {
  segments: { label: string; value: number; color: string }[];
  size?: number;
  centerLabel?: string;
  centerSub?: string;
  ariaLabel: string;
}) {
  const total = Math.max(
    1,
    segments.reduce((s, x) => s + x.value, 0),
  );
  const r = 42;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  return (
    <figure className="donut" aria-label={ariaLabel} style={{ width: size }}>
      <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--line)" strokeWidth="10" />
        {segments.map((seg) => {
          const frac = seg.value / total;
          const dash = frac * circ;
          const el = (
            <circle
              key={seg.label}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 50 50)"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      {centerLabel !== undefined && (
        <div className="donut__center">
          <span className="donut__value">{centerLabel}</span>
          {centerSub && <span className="donut__sub">{centerSub}</span>}
        </div>
      )}
    </figure>
  );
}

/* ── Legend ──────────────────────────────────────────────────────────────── */
export interface LegendItem {
  label: string;
  color: string;
  value?: string;
  /** Stable id reported to `onActiveChange` (interactive legends only). */
  id?: string;
}

/**
 * A swatch + label + value list. Passing `onActiveChange` makes it interactive:
 * each item with an `id` reports hover / keyboard focus so a sibling chart can
 * highlight the matching slice and show its tooltip. `activeId` marks the item
 * currently highlighted (from either side). `describedBy` is the tooltip id,
 * wired onto the active item for assistive tech.
 */
export function Legend({
  items,
  activeId,
  onActiveChange,
  describedBy,
}: {
  items: LegendItem[];
  activeId?: string | null;
  onActiveChange?: (id: string | null) => void;
  describedBy?: string;
}) {
  return (
    <ul className="legend">
      {items.map((it) => {
        const interactive = onActiveChange !== undefined && it.id !== undefined;
        const active = interactive && activeId === it.id;
        return (
          <li
            key={it.id ?? it.label}
            data-active={active ? '' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-describedby={active ? describedBy : undefined}
            onMouseEnter={interactive ? () => onActiveChange(it.id!) : undefined}
            onMouseLeave={interactive ? () => onActiveChange(null) : undefined}
            onFocus={interactive ? () => onActiveChange(it.id!) : undefined}
            onBlur={interactive ? () => onActiveChange(null) : undefined}
          >
            <span className="legend__dot" style={{ background: it.color }} aria-hidden="true" />
            <span className="legend__label">{it.label}</span>
            {it.value && <span className="legend__value num">{it.value}</span>}
          </li>
        );
      })}
    </ul>
  );
}
